import Link from "@/src/common/components/Link"
import { UNCOMPLETED_PROFILE_DEMO_FORM_URL } from "@/src/sb/constants/configs"
import { SignInData } from "@/src/sb/core/models/auth"
import Image from "next/legacy/image"
import React, { ChangeEvent, ReactNode, SyntheticEvent } from "react"

import { auth, pages } from "../../../constants/copy"
import Button from "../../Button"
import { TextInput } from "../../Input"
import { PasswordInput } from "../../Input"
import AuthForm from "../AuthForm"

const { signin } = auth

type SigninFields = keyof SignInData

type SigninErrors = Record<SigninFields, string[]>

export type SigninFormProps = {
  signinDisabled: boolean
  signupLink?: string
  loading?: boolean
  title?: string
  errors?: Partial<SigninErrors> | null
  handleSubmit: (e: React.FormEvent) => void
  handleChange?: (e: ChangeEvent<HTMLFormElement>) => void
  handleGoogleLogin?: (e?: SyntheticEvent) => void
  handleAppleLogin?: (e?: SyntheticEvent) => void
  resendVerification?: () => void
  formErrorMsg?: string
  formErrorCode?: "no_such_account" | "account_not_active" | string
  headerAddOn?: ReactNode
}

const SigninForm = ({ headerAddOn, ...props }: SigninFormProps) => {
  const {
    loading = false,
    handleGoogleLogin,
    handleAppleLogin,
    signupLink,
    errors = {},
    handleChange,
    signinDisabled,
    handleSubmit,
    resendVerification,
    formErrorCode,
    formErrorMsg
  } = props

  return (
    <>
      <header className="text-center mb-5">
        <AuthForm.Title text={signin.title} />
        <AuthForm.RelatedFormSection
          text={signin.dontHaveAnAccount}
          href={signupLink || UNCOMPLETED_PROFILE_DEMO_FORM_URL}
          linkLabel={signin.signupLinkLabel}
        />

        {headerAddOn ?? null}
      </header>

      <Button
        type="tertiary"
        stretch
        roundness={"edge"}
        onClick={handleGoogleLogin}
        leftIcon={
          <Image
            src="/assets/icons/google.svg"
            layout="fixed"
            width={18}
            height={18}
            alt="Google"
          />
        }
      >
        {signin.googleBtnLabel}
      </Button>
      <div className="mt-2">
        <Button
          type="primary"
          stretch
          roundness={"edge"}
          onClick={handleAppleLogin}
          leftIcon={
            <Image
              src="/assets/icons/apple.svg"
              layout="fixed"
              width={18}
              height={18}
              alt="Apple"
            />
          }
        >
          {signin.appleBtnLabel}
        </Button>
      </div>

      <AuthForm.LineBreaker />

      <form
        className="mt-3 text-left"
        onChange={handleChange}
        onSubmit={handleSubmit}
      >
        <AuthForm.FormItem labelText="Email" labelHTMLFor="username">
          <TextInput name="username" type="email" errors={errors?.username} />
        </AuthForm.FormItem>

        <AuthForm.FormItem labelText="Password" labelHTMLFor="password">
          <PasswordInput name="password" errors={errors?.password} />
        </AuthForm.FormItem>
        <div className="text-right">
          <Link legacyBehavior href={pages.forgotPassword}>
            <a className="text-accent-600 text-p_14 text-right  inline-block hover:underline w-fit">
              Forgot Password
            </a>
          </Link>
        </div>

        {formErrorCode === "account_not_active" && (
          <AuthForm.FormError>
            Looks like your email address has not been confirmed. Please check
            your email for confirmation link.{" "}
            <button
              onClick={resendVerification}
              type="button"
              className="text-accent-600 hover:underline"
            >
              Resend
            </button>
          </AuthForm.FormError>
        )}

        {formErrorCode && formErrorCode !== "account_not_active" && (
          <AuthForm.FormError>{formErrorMsg}</AuthForm.FormError>
        )}
        <div className="mt-10">
          <Button
            id="signin"
            type="primary"
            htmlType="submit"
            stretch
            roundness={"edge"}
            loading={loading}
            disabled={signinDisabled || loading}
          >
            Sign in
          </Button>
        </div>
      </form>
    </>
  )
}

export default SigninForm
